import { DatePipe } from '@angular/common';
import { afterNextRender, Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import {
  AlertComponent,
  InputLabelComponent,
  LoaderComponent,
  RadioComponent,
  RadioDescription,
  SelectComponent,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import {
  AvailableLicense,
  AvailableLicensesSelectElements,
  ChangeLicenseCase
} from 'mbs-ui-shared@components/licenses/components/select-license-modal/types';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { DeepPartial, LicenseSettings, LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import Computer from 'mbs-ui-shared@models/Computer';
import { AvailableLicenses, AvailableLicenseType, AvailablePaidLicense, ComputerLicensePool } from 'mbs-ui-shared@models/License';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { forkJoin, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'mbs-select-license-form',
  templateUrl: './select-license-form.component.html',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    TextEllipsisComponent,
    LoaderComponent,
    CanPipe,
    InputLabelComponent,
    AlertComponent,
    I18NextPipe,
    SelectComponent,
    RadioComponent,
    RadioDescription
  ]
})
export class SelectLicenseFormComponent {
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private readonly destroyRef = inject(DestroyRef);
  protected licensesService = inject(LicensesService);
  private datePipe = inject(DatePipe);
  private i18n = inject(I18NextPipe);
  private ability = inject(AbilityService);
  private companiesFacade = inject(CompaniesFacade);

  administratorMode = input<boolean>(false);
  pool = input<ComputerLicensePool>();
  computer = input<Computer>();
  kind = input<number>();
  licenseTypeId = input<number>();
  form = input.required<
    FormGroup<{
      changeLicenseCase: FormControl<ChangeLicenseCase>;
      selectedLicense: FormControl<string>;
      selectedAnotherPoolLicense: FormControl<boolean>;
      selectedLicenseType: FormControl<number>;
    }>
  >();

  companyId = input<string>();

  public isPostPayment = this.licensesSignalFacade.isPostPayment;

  public readonly isNil = isNil;
  public readonly ChangeLicenseCase = ChangeLicenseCase;

  public licenseTypes = this.licensesSignalFacade.supportedLicenseTypes;
  public licenseTypesLoading = this.licensesSignalFacade.supportedLicenseTypesLoading;
  public licenseTypesLoaded = this.licensesSignalFacade.supportedLicenseTypesLoaded;
  public licenseTypesWithDisplayName = this.licensesSignalFacade.supportedLicenseTypesWithDisplayName;

  public availableLicenses = signal<AvailableLicensesSelectElements | null>(null);
  public availablePaidLicenses = signal<AvailablePaidLicense[]>([]);

  public loading = signal(false);
  public companyLoading = signal(false);

  public isOnlyOneOptionAvailable = signal(false);

  public companyHasRestrictions = signal(false);

  private companyLicenseSettings = signal<DeepPartial<LicenseSettings> | null>(null);
  private selectedLicenseTypeValue = signal<number | null>(null);
  private licensesSubscription: Subscription;

  public isCompanyPool = computed(() => this.pool()?.name === 'Company pool');
  public isUserPool = computed(() => this.pool()?.name === 'User pool');
  public hid = computed(() => this.computer()?.hid);

  public licenseSettingsLink = computed(() => {
    const id = this.pool()?.subject?.id;
    return this.isCompanyPool() ? `AP/Companies/${id}` : `AP/Users/${id}`;
  });

  public companyLicenseSettingsLink = computed(() => `AP/Companies/${this.companyId()}`);

  public isShownLicenseSettingsLink = computed(
    () =>
      (this.isUserPool() && this.ability.can('read', 'EnableUserPool') && this.ability.can('read', 'UsersCreateEdit')) ||
      (this.isCompanyPool() && this.ability.can('read', 'ManageCompanies'))
  );

  public isNotAllowedLicense = computed(() => {
    const selected = this.selectedLicenseTypeValue();
    return !isNil(selected) && !this.licenseTypes()?.find((lt) => lt.id === selected)?.isAllowForCompany;
  });

  public selectedLicenseType = computed<LicenseType>(() => {
    const v = this.selectedLicenseTypeValue();
    return !isNil(v) ? v : this.licenseTypeId();
  });

  private rmmCESelected = computed(
    () =>
      this.ability.can('read', PermissionsEnum.RmmPerEndpoint) &&
      this.ability.can('read', PermissionsEnum.RmmCeEnabled) &&
      this.selectedLicenseType() === LicenseType.RmmCePerEndpoint
  );

  constructor() {
    effect(() => this.companyId() && this.setHasRestrictions());

    effect(() => {
      const types = this.licenseTypes();
      const companyDefault = this.companyLicenseSettings()?.defaultLicense;

      if (types === null || this.companyLoading()) return;

      untracked(() => {
        this.initSelectedLicenseType(types, companyDefault);
        if (isNil(this.form().value.selectedLicenseType)) {
          this.licensesSubscription?.unsubscribe();
        }
      });
    });

    effect(() => {
      const hid = this.hid();
      const kind = this.kind();
      this.companyId(); // track so effect re-fires on companyId change
      if (!hid || isNil(kind)) return;
      untracked(() => this.setLicenseTypes());
    });

    effect(() => {
      const licenseType = this.selectedLicenseTypeValue();
      if (isNil(licenseType)) return;

      untracked(() => this.setLicenses());
    });

    afterNextRender(() => this.handleFormValueChanges());
  }

  protected handleFormValueChanges() {
    if (!this.isPostPayment()) {
      this.handleChangeLicenseType();
    }
    this.handleChangeLicenseCase();
  }

  private handleChangeLicenseCase() {
    this.form()
      .controls.changeLicenseCase.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: ChangeLicenseCase) => {
        this.form().controls.selectedLicense.patchValue((this.availableLicenses()?.[value] as AvailableLicense)?.licenseId);
        this.form().controls.selectedAnotherPoolLicense.patchValue(null);
      });
  }

  public isGlobalPool(license: AvailablePaidLicense) {
    return license?.poolName === 'Global pool';
  }

  protected setLicenseTypes(): void {
    const hid = this.hid();
    const kind = this.kind();
    if (!hid || isNil(kind)) return;
    this.licensesSignalFacade.loadSupportedLicenseTypes(this.getSupportedLicenseTypesParams());
  }

  protected getSupportedLicenseTypesParams(): { hid: string; kind: number; companyId?: string; userId?: string } {
    return { hid: this.hid(), kind: this.kind() };
  }

  private initSelectedLicenseType(types: AvailableLicenseType[], companyDefault: LicenseType | undefined): void {
    const currentId = types.find((type) => type.id === this.selectedLicenseType())?.id;
    const companyDefaultId = companyDefault !== LicenseType.NA ? types.find((type) => type.id === companyDefault)?.id : null;
    const defaultId = this.companyId() ? (companyDefaultId ?? types.find((type) => type.isDefault)?.id) : null;

    this.form().controls.selectedLicenseType.setValue(currentId ?? defaultId ?? null);
  }

  protected setLicenses(): void {
    if (isNil(this.selectedLicenseType()) || !this.hid()) {
      return;
    }

    this.loading.set(true);
    this.form().get('changeLicenseCase').setValue(null);

    this.licensesSubscription = forkJoin({
      availablePaidLicenses: this.getGetAvailablePaidLicensesRequest(this.selectedLicenseType()),
      availableLicenses: this.getGetAvailableLicensesRequest(this.selectedLicenseType())
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe((result) => {
        this.availablePaidLicenses.set(
          result.availablePaidLicenses?.map((license) => ({
            ...license,
            selectedLabel: license.name ? `${license.poolName}: ${license.name}` : license.poolName
          }))
        );
        this.setAvailableLicenses(result.availableLicenses);
      });
  }

  private handleChangeLicenseType() {
    this.selectedLicenseTypeValue.set(this.form().value.selectedLicenseType ?? null);
    this.form()
      .controls.selectedLicenseType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.selectedLicenseTypeValue.set(value));
  }

  private setAvailableLicenses(licenses: AvailableLicenses): void {
    const paidLicense = licenses?.paidLicense;
    const rmmCE = this.rmmCESelected();

    const licenseOptions: AvailableLicensesSelectElements = {
      [ChangeLicenseCase.PAID]: Boolean(paidLicense) && {
        label: this.i18n.transform('shared:licenses.management.selectLicenseModalComponent:availablePaidLicense', {
          pool: paidLicense?.poolName,
          validUntil: this.datePipe.transform(paidLicense?.validUntil)
        }),
        value: ChangeLicenseCase.PAID,
        licenseId: paidLicense.licenseId
      },
      [ChangeLicenseCase.BUY]: (rmmCE || this.ability.can('read', 'LicenseBuy')) && {
        label: rmmCE
          ? this.i18n.transform('shared:licenses.freeLicense')
          : this.i18n.transform('shared:licenses.management.selectLicenseModalComponent:buyNewLicense', {
              price: licenses?.newLicensePrice,
              interpolation: { escapeValue: false }
            }),
        value: ChangeLicenseCase.BUY
      },
      [ChangeLicenseCase.TRIAL]: Boolean(licenses?.trialLicense && !rmmCE) && {
        label: this.i18n.transform('shared:licenses.management.selectLicenseModalComponent:trial'),
        value: ChangeLicenseCase.TRIAL,
        licenseId: licenses?.trialLicense.licenseId
      },
      [ChangeLicenseCase.POOL]: Boolean(this.availablePaidLicenses()?.length) && {
        label: this.i18n.transform('shared:licenses.management.selectLicenseModalComponent:withdrawFromAnotherPool'),
        value: ChangeLicenseCase.POOL
      }
    };

    this.availableLicenses.set(licenseOptions);
    const entries = Object.values(licenseOptions).filter(Boolean);
    const selected = this.administratorMode() ? entries.find((el) => el.value !== ChangeLicenseCase.BUY)?.value : entries[0]?.value;
    this.form().controls.changeLicenseCase.patchValue(selected);
    this.isOnlyOneOptionAvailable.set(entries.length === 1);
  }

  protected getGetAvailablePaidLicensesRequest(licenseType: LicenseType) {
    return this.licensesService.getAvailablePaidLicenses({
      hid: this.hid(),
      licenseTypeIds: [licenseType] as LicenseType[]
    });
  }

  protected getGetAvailableLicensesRequest(licenseType: LicenseType) {
    return this.licensesService.getAvailableLicenses(this.hid(), licenseType);
  }

  private setHasRestrictions() {
    this.companyLoading.set(true);

    this.companiesFacade
      .getCompany(this.companyId())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.companyLoading.set(false))
      )
      .subscribe((company) => {
        const licenseSettings = company?.licenseSettings;
        this.companyLicenseSettings.set(licenseSettings ?? null);
        this.companyHasRestrictions.set(licenseSettings?.allowedLicenses?.some((licenseType) => !licenseType.isAllow));
      });
  }
}
